import * as React from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ChatCircle as ChatCircleIcon, PaperPlaneTilt as PaperPlaneTiltIcon, Lock as LockIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import type { Conversation } from '@/types/conversation';
import axios from '@/lib/v2/axios';
import { useCommunityRole } from '@/hooks/v2/use-community-role';
import { useUser } from '@/hooks/v2/use-user';
import { SectionLabel } from './request-details-card';

dayjs.extend(relativeTime);

interface RequestConversationProps {
  conversationId?: number | string | null;
  requestId?: number | string | null;
}

export function RequestConversation({ conversationId, requestId }: RequestConversationProps): React.JSX.Element | null {
  const theme = useTheme();
  const { hasBoardLevelAccess } = useCommunityRole();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [messageText, setMessageText] = React.useState('');
  const [isInternal, setIsInternal] = React.useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  const { data: conversation, isLoading } = useQuery<Conversation>({
    queryKey: ['conversation', conversationId],
    queryFn: async () => {
      const response = await axios.get(`/api/v1/conversations/${conversationId}`);
      return response.data;
    },
    enabled: !!conversationId,
    refetchInterval: 30_000,
  });

  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation?.messages?.length]);

  const sendMessageMutation = useMutation({
    mutationFn: async ({ message, visibility }: { message: string; visibility: 'public' | 'internal' }) => {
      const response = await axios.post(`/api/v1/conversations/${conversationId}/messages`, {
        message,
        visibility,
      });
      return response.data;
    },
    onSuccess: () => {
      setMessageText('');
      setIsInternal(false);
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    },
    onError: () => {
      toast.error('Failed to send message');
    },
  });

  const handleSend = () => {
    const trimmed = messageText.trim();
    if (!trimmed) return;
    sendMessageMutation.mutate({ message: trimmed, visibility: isInternal ? 'internal' : 'public' });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSend();
    }
  };

  if (!conversationId) return null;

  const messages = conversation?.messages ?? [];

  return (
    <Box>
      <SectionLabel icon={<ChatCircleIcon size={14} />}>
        Activity
      </SectionLabel>

      {hasBoardLevelAccess && (
        <Typography color="text.disabled" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
          Internal messages are only visible to board members and managers
        </Typography>
      )}

      {/* Message list */}
      <Box
        sx={{
          mt: 2,
          maxHeight: 420,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!isLoading && messages.length === 0 && (
          <Typography color="text.disabled" sx={{ py: 3, textAlign: 'center', fontStyle: 'italic' }} variant="body2">
            No messages yet. Be the first to add an update.
          </Typography>
        )}

        {messages.map((msg) => {
          const isInternalMsg = msg.visibility === 'internal';
          const isOwnMessage = msg.created_by?.id === user?.id;
          return (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                gap: 0.5,
              }}
            >
              {/* Message sender info - only for received messages */}
              {!isOwnMessage && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', pl: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }} variant="body2">
                    {msg.created_by?.first_name} {msg.created_by?.last_name}
                  </Typography>
                  {msg.created_by?.community?.roles?.includes('community_manager') && (
                    <Chip
                      label="Manager"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.6rem' }}
                    />
                  )}
                  {msg.created_by?.community?.roles?.includes('board_member') && (
                    <Chip
                      label="Board Member"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.6rem' }}
                    />
                  )}
                </Stack>
              )}
              
              {/* Message bubble */}
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                  width: '100%',
                  alignItems: 'flex-end',
                }}
              >
                {!isOwnMessage && (
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: '0.75rem',
                      bgcolor: 'action.hover',
                    }}
                  >
                    {msg.created_by?.first_name?.[0]?.toUpperCase() ?? '?'}
                  </Avatar>
                )}
                
                <Box sx={{ position: 'relative', maxWidth: '70%', overflow: 'visible' }}>
                  <Box
                    sx={{
                      backgroundColor: isOwnMessage ? 'primary.main' : 'action.hover',
                      color: isOwnMessage ? 'primary.contrastText' : 'text.primary',
                      borderRadius: isOwnMessage
                        ? '16px 16px 4px 16px'
                        : '16px 16px 16px 4px',
                      px: 2,
                      py: 1,
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        color: isOwnMessage ? 'primary.contrastText' : 'text.primary',
                      }}
                    >
                      {msg.content}
                    </Typography>
                    
                    {/* Internal badge */}
                    {isInternalMsg && (
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.75 }}>
                        <LockIcon size={10} />
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            opacity: isOwnMessage ? 0.7 : 1,
                          }}
                        >
                          Internal
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                  
                  {/* iMessage-style curved tail */}
                  <Box
                    component="span"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      ...(isOwnMessage ? { right: -10, transform: 'scaleX(-1)' } : { left: -10 }),
                    }}
                  >
                    <svg width="16" height="20" viewBox="0 0 16 20" style={{ display: 'block' }}>
                      <path
                        d="M0 20 C0 20 4 20 8 14 C10 10 10 6 10 0 L16 0 C16 0 12 4 10 10 C8 16 6 20 0 20 Z"
                        fill={isOwnMessage ? theme.palette.primary.main : theme.palette.action.hover}
                      />
                    </svg>
                  </Box>
                </Box>
              </Stack>
              
              {/* Timestamp */}
              <Typography color="text.disabled" sx={{ fontSize: '0.7rem', px: 1 }}>
                {dayjs(msg.created_at).fromNow()}
              </Typography>
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {/* Compose area */}
      <Divider sx={{ mt: 2, mb: 1.5 }} />
      <Stack spacing={1}>
        {hasBoardLevelAccess && (
          <FormControlLabel
            control={
              <Switch
                checked={isInternal}
                onChange={(e) => { setIsInternal(e.target.checked); }}
                size="small"
              />
            }
            label={
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <LockIcon size={12} />
                <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">Internal</Typography>
              </Stack>
            }
            sx={{ m: 0 }}
          />
        )}
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
          <TextField
            disabled={sendMessageMutation.isPending}
            fullWidth
            maxRows={4}
            minRows={2}
            multiline
            onChange={(e) => { setMessageText(e.target.value); }}
            onKeyDown={handleKeyDown}
            placeholder={
              isInternal
                ? 'Internal note (not visible to residents)...'
                : 'Write an update or reply...'
            }
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                ...(isInternal && { borderColor: 'warning.main' }),
              },
            }}
            value={messageText}
          />
          <IconButton
            color="primary"
            disabled={!messageText.trim() || sendMessageMutation.isPending}
            onClick={handleSend}
            size="small"
            sx={{ mb: 0.5 }}
          >
            {sendMessageMutation.isPending ? (
              <CircularProgress size={18} />
            ) : (
              <PaperPlaneTiltIcon size={18} />
            )}
          </IconButton>
        </Stack>
        <Typography color="text.disabled" sx={{ textAlign: 'right', fontSize: '0.7rem' }}>
          Cmd+Enter to send
        </Typography>
      </Stack>
    </Box>
  );
}
